import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config/api';

const logger = {
  warn: (...args) => process.env.NODE_ENV !== 'production' && console.warn(...args),
  error: (...args) => process.env.NODE_ENV !== 'production' && console.error(...args),
};

const AuthContext = createContext(null);

// Axios interceptor: attach token from sessionStorage to every request
axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('auth_token');
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const token = sessionStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      sessionStorage.removeItem('auth_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token, user: userData } = response.data;
    sessionStorage.setItem('auth_token', token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token, user: newUser } = response.data;
    sessionStorage.setItem('auth_token', token);
    setUser(newUser);
    return newUser;
  };

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // Continue cleanup even if server call fails
    }
    sessionStorage.removeItem('auth_token');
    setUser(null);
  }, []);

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const value = {
    user,
    token: sessionStorage.getItem('auth_token'),
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isArtist: user?.role === 'artist',
    isBuyer: user?.role === 'buyer',
    isAdmin: user?.role === 'admin',
    isMember: user?.is_member,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
